import { Injectable } from "@angular/core";
import { ENCODABLE_TYPE_ERRORS } from "shared/broker-client-services";
import {
  PRIMITIVE,
  ClientMessageBrokerFactory,
  ClientMessageBroker,
  FnArg,
  UiArguments,
  FnTransferableArg,
} from "shared/provider-webworker";
import { IRateService, CHANNEL_RATE } from "../IRateService";
import { Rate } from "../model/Rate";
import { Observable, Subject } from "rxjs";
import { RateSubscription } from "../model/RateSubscription";
import { RateCache } from "./RateCache";
import { Product, Tenor } from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class RateReplicaService implements IRateService {
  protected broker: ClientMessageBroker;
  protected rateEventSubject: Subject<Array<Rate>> = new Subject<Array<Rate>>();

  constructor(protected factory: ClientMessageBrokerFactory, public rateCache: RateCache) {
    this.initService();
  }

  protected initService(): void {
    this.broker = this.factory.createMessageBroker(CHANNEL_RATE, true, true);

    let refreshChannel = new MessageChannel();
    let clientRefreshPort: MessagePort = refreshChannel.port1;
    let serviceRefreshPort: MessagePort = refreshChannel.port2;

    let request = new UiArguments("initPort", [
      new FnTransferableArg(serviceRefreshPort, PRIMITIVE),
    ]);

    clientRefreshPort.addEventListener("message", (event) => {
      let rates: Array<Rate> = event.data as Array<Rate>;
      rates = rates.map((rate) => this.mapRate(rate));
      let changedRates = this.rateCache.addRates(rates);
      if (changedRates.length > 0) {
        this.rateEventSubject.next(changedRates);
      }
    });

    this.broker.runOnService(request, null);
    clientRefreshPort.start();
  }

  public getRateEventSubject(): Subject<Array<Rate>> {
    return this.rateEventSubject;
  }

  public getSpotRate(productCode: string): Promise<Rate> {
    let request = new UiArguments("getSpotRate");

    let arg1: FnArg = new FnArg(productCode, PRIMITIVE);
    request.args = [arg1];

    return this.runOnService(request, this.mapRate);
  }

  public getSpotRates(productCodes: Array<string>): Promise<Array<Rate>> {
    let request = new UiArguments("getSpotRates");

    let arg1: FnArg = new FnArg(productCodes, PRIMITIVE);
    request.args = [arg1];

    return this.runOnService(request, this.mapRate);
  }

  public getRate(
    productCode: string,
    product: Product,
    tenor: Tenor,
    valueDate: Date,
  ): Promise<Rate> {
    let request = new UiArguments("getRate");

    let arg1: FnArg = new FnArg(productCode, PRIMITIVE);
    let arg2: FnArg = new FnArg(product, PRIMITIVE);
    let arg3: FnArg = new FnArg(tenor, PRIMITIVE);
    let arg4: FnArg = new FnArg(valueDate, PRIMITIVE);
    request.args = [arg1, arg2, arg3, arg4];

    return this.runOnService(request, this.mapRate);
  }

  public getRates(productCodes: Array<RateSubscription>): Promise<Array<Rate>> {
    let request = new UiArguments("getRates");

    let arg1: FnArg = new FnArg(productCodes, PRIMITIVE);
    request.args = [arg1];

    return this.runOnService(request, this.mapRate);
  }

  public pauseStream(): Promise<void> {
    let request = new UiArguments("pauseStream");

    request.args = [];

    return this.runOnService(request);
  }

  public resumeStream(): Promise<void> {
    let request = new UiArguments("resumeStream");

    request.args = [];

    return this.runOnService(request);
  }

  public isStreamingPaused(): Promise<boolean> {
    let request = new UiArguments("isStreamingPaused");

    request.args = [];

    return this.runOnService(request);
  }

  protected runOnService(request: UiArguments, map: any = null): Promise<any> {
    return new Promise<any>((res, rej) => {
      this.broker.runOnService(request, PRIMITIVE).then((value: any) => {
        if (value && value.enType && value.enType == ENCODABLE_TYPE_ERRORS) {
          rej(value);
        } else {
          if (map && value) {
            if (value instanceof Array) {
              let values: Array<any> = value as Array<any>;
              values = values.map(map);
            } else {
              value = map(value);
            }
          }
          res(value);
        }
      });
    });
  }

  public mapRate(object: Object): Rate {
    return Object.assign(new Rate(), object);
  }
}
